// src/design/designCenterLocale.js
//
// Translation table for the DesignCenter AI assistant's stock prompts.
//
// Phase 3 of the language-preference rollout. Option A: hardcoded
// translations of ~20 fixed strings (assistant opening prompts, system
// messages like "Image updated.", placeholder text). The user's preferred
// language comes from u_preferred_language on the users row (loaded via the
// existing /api/users/profile endpoint, which returns u.*).
//
// Strings NOT translated here (intentional):
//   - All UI chrome (buttons, menus, navigation, modals) — those stay English
//   - The AI-generated content (captions, landing pages, follow-up question)
//     — those are translated by the backend AI prompts using
//     NATIVE_LANGUAGE_RULE + the user's preferred language hint
//   - User-typed text — they write in whatever language they want
//
// Adding a new language: copy the 'en' block, translate every key. Then add
// the language code to SUPPORTED in userLanguagePreference.js. Strings are
// short enough that LLM-assisted translation + a native-speaker review is
// the right workflow.
//
// String key → meaning:
//   sections.<key>.opening  — first message the assistant sends when the
//                             user enters a section (one per content category)
//   image_updated           — banner after the user swaps the image
//   follow_up_fallback      — used when the AI follow-up call fails
//   generating              — shown while captions are being generated
//   updating                — shown while a revision is being applied
//   results_intro           — "Here's your headline for the image:"
//   results_updated_intro   — "Updated headline:"
//   results_outro           — closing prompt after captions are shown
//   adjust_or_preview       — fallback CTA when the conversation has finished
//   error_generic           — "Something went wrong. Please try again."
//   error_network           — "Network error..."
//   input_placeholder       — text input placeholder

const SECTION_OPENINGS_EN = {
  how_things_work: `Let's break down how something in your world works — a process, a system, a product, or a decision your customers face. When people understand what goes into it, they trust the expert who explained it. What do you want to demystify for your audience?`,
  how_to_tips: `Let's give your audience something they can actually use — a tip, a shortcut, a maintenance trick, or a "wish I knew this sooner" moment. The best tips make people save your post and come back to it. What's something your customers should know how to do?`,
  problems_solutions: `Every customer has a pain point they're dealing with — something broken, something they're worried about, or something costing them money. When you name the problem and show the fix, you become the person they call. What's a problem your customers run into that you solve?`,
  advice_insights: `You've seen things in your industry that most people haven't. Maybe it's a common mistake people make, a misconception that costs them, or a lesson you've learned the hard way. That kind of insight builds real trust. What's a piece of advice you wish every customer knew?`,
  specials_discounts: `Let's make your offer impossible to scroll past. Whether it's a discount, a bundle, a free consultation, or a seasonal deal — the key is making people feel like they'd be crazy to miss it. What's the offer and why should someone act on it right now?`,
  stories_experiences: `People connect with stories more than sales pitches. A job that went sideways and you saved it, a customer who thanked you, a moment that reminded you why you do this — those are the posts people share. What's a story from your business worth telling?`,
  community_relations: `People want to support businesses that show up for their community — sponsoring the little league, volunteering after a storm, partnering with a local charity, or just being the business everyone knows by name. What's something you're doing in your community that people should see?`,
  humor_personality: `The posts that get the most engagement aren't always about your services — they're the ones that make people laugh, nod, or tag a friend. A workplace moment, an industry joke, a "you had to be there" story. What's something funny, relatable, or real about your day-to-day?`,
};

const SECTION_OPENINGS_TH = {
  how_things_work: `มาช่วยลูกค้าเข้าใจอะไรบางอย่างในงานของคุณกันค่ะ — กระบวนการ ระบบ สินค้า หรือการตัดสินใจที่ลูกค้าต้องเจอ พอเขาเข้าใจว่ามีอะไรอยู่เบื้องหลัง เขาก็จะเชื่อใจคนที่อธิบายเป็น อยากเล่าเรื่องอะไรให้ลูกค้าเข้าใจดี?`,
  how_to_tips: `มาให้ลูกค้าได้อะไรที่เอาไปใช้ได้จริงกันค่ะ — ทิป ทางลัด เคล็ดดูแลรักษา หรือ "รู้งี้ทำตั้งนานแล้ว" โพสต์ทิปดีๆ คนชอบเซฟไว้ดูซ้ำ มีอะไรที่อยากแนะนำลูกค้าให้ทำเป็นไหมคะ?`,
  problems_solutions: `ลูกค้าทุกคนมีปัญหาที่กำลังเจอ — ของพัง กังวลใจ หรือเสียเงินโดยไม่จำเป็น พอเราชี้ปัญหาให้เห็นและบอกวิธีแก้ ลูกค้าก็จะคิดถึงเราเป็นคนแรก มีปัญหาอะไรของลูกค้าที่คุณช่วยแก้ได้บ้างคะ?`,
  advice_insights: `คุณเห็นอะไรในวงการที่คนทั่วไปไม่เห็น อาจจะเป็นความเข้าใจผิดที่ทำให้เสียเงิน ความผิดพลาดที่คนทำบ่อย หรือบทเรียนที่คุณได้มาจากประสบการณ์จริง ความรู้แบบนี้สร้างความเชื่อใจได้มากค่ะ มีคำแนะนำอะไรอยากให้ลูกค้าทุกคนรู้บ้าง?`,
  specials_discounts: `มาทำโปรโมชั่นให้คนหยุดเลื่อนหน้าจอกันค่ะ — ไม่ว่าจะเป็นส่วนลด เซ็ตคู่ ปรึกษาฟรี หรือดีลตามฤดูกาล หัวใจคือทำให้รู้สึกว่าพลาดไม่ได้ มีโปรอะไรอยู่ และทำไมต้องรีบใช้ตอนนี้คะ?`,
  stories_experiences: `เรื่องราวจริงๆ ดึงคนได้มากกว่าการขายตรงๆ ค่ะ — งานที่เกือบจะเสียแต่กู้คืนได้ ลูกค้าที่ขอบคุณ หรือเหตุการณ์ที่ทำให้คุณรักงานนี้อีกครั้ง โพสต์แบบนี้คนชอบแชร์ มีเรื่องอะไรในร้านอยากเล่าให้ฟังบ้างคะ?`,
  community_relations: `คนอยากสนับสนุนร้านที่ดูแลชุมชน — สนับสนุนทีมเด็กในย่าน ช่วยเหลือหลังเกิดเหตุการณ์ ร่วมงานการกุศล หรือเป็นร้านที่ทุกคนในละแวกรู้จัก คุณกำลังทำอะไรในชุมชนที่อยากให้คนเห็นบ้างคะ?`,
  humor_personality: `โพสต์ที่คนเอนเกจสูงสุด ไม่ใช่โพสต์ขายของเสมอไป — แต่เป็นโพสต์ที่ทำให้คนหัวเราะ พยักหน้า หรือแท็กเพื่อน เหตุการณ์ในร้าน มุกในวงการ หรือเรื่อง "ต้องอยู่ที่นั่นถึงจะเก็ต" มีอะไรในงานประจำวันที่ตลก น่ารัก หรือเรียล อยากเล่าบ้างคะ?`,
};

const SECTION_OPENINGS_ES = {
  how_things_work: `Vamos a explicar cómo funciona algo de tu mundo — un proceso, un sistema, un producto, o una decisión que tus clientes enfrentan. Cuando la gente entiende lo que hay detrás, confía en el experto que se lo explicó. ¿Qué te gustaría que tu audiencia entendiera mejor?`,
  how_to_tips: `Vamos a darle a tu audiencia algo que realmente puedan usar — un tip, un atajo, un truco de mantenimiento, o ese "ojalá hubiera sabido esto antes". Los mejores tips hacen que la gente guarde tu post y vuelva a verlo. ¿Qué debería saber hacer tu cliente?`,
  problems_solutions: `Todo cliente tiene un dolor que lo está afectando — algo roto, algo que le preocupa, o algo que le está costando dinero. Cuando nombras el problema y muestras la solución, te convertís en la persona a la que llaman. ¿Qué problema resuelven que tus clientes están enfrentando?`,
  advice_insights: `Has visto cosas en tu industria que la mayoría no ve. Tal vez un error común que la gente comete, un mito que les sale caro, o una lección que aprendiste de la forma difícil. Ese tipo de conocimiento genera confianza real. ¿Qué consejo te gustaría que cada cliente supiera?`,
  specials_discounts: `Vamos a hacer tu oferta imposible de ignorar. Sea un descuento, un combo, una consulta gratis, o una promo de temporada — la clave es hacer sentir que sería una locura perdérsela. ¿Cuál es la oferta y por qué deberían actuar ahora?`,
  stories_experiences: `La gente conecta con historias más que con ventas. Un trabajo que casi se va a la basura y lo salvaron, un cliente que les agradeció, un momento que les recordó por qué hacen esto — esos son los posts que la gente comparte. ¿Qué historia de tu negocio vale la pena contar?`,
  community_relations: `La gente quiere apoyar negocios que están presentes en su comunidad — patrocinar el equipo de niños del barrio, ayudar después de una tormenta, colaborar con una organización benéfica local, o simplemente ser el negocio que todos conocen por nombre. ¿Qué estás haciendo en tu comunidad que la gente debería ver?`,
  humor_personality: `Los posts con más engagement no siempre son sobre tus servicios — son los que hacen reír, asentir, o etiquetar a un amigo. Un momento en el trabajo, un chiste del rubro, una historia de "tenías que estar ahí". ¿Qué hay de gracioso, relacionable, o real en tu día a día?`,
};

const SECTION_OPENINGS_JA = {
  how_things_work: `お客様の世界で何かがどう動くのか、説明してみましょう — プロセス、システム、商品、お客様が直面する判断など。何が裏にあるか分かると、説明してくれた専門家を信頼するようになります。お客様に分かってほしいことは何ですか?`,
  how_to_tips: `お客様が実際に使える何かを届けましょう — コツ、近道、メンテのちょっとした技、「もっと早く知りたかった」ポイントなど。いい tip は保存されて何度も見返されます。お客様に知っておいてほしいやり方はありますか?`,
  problems_solutions: `お客様にはみんな抱えている悩みがあります — 壊れてるもの、心配ごと、お金がかかってること。問題を言葉にして解決策を見せると、最初に呼ばれる人になれます。あなたが解決できる、お客様の悩みは何ですか?`,
  advice_insights: `業界の中で、普通の人には見えないものが見えているはず。よくある間違い、損する誤解、苦労して学んだ教訓など。そういう視点が本当の信頼を生みます。お客様みんなに知ってほしいアドバイスは?`,
  specials_discounts: `スクロールを止めさせるオファーを作りましょう — 割引、セット、無料相談、季節限定など。ポイントは「逃したらもったいない」と感じてもらうこと。オファーの内容と、今すぐ行動すべき理由は?`,
  stories_experiences: `人は売り込みより物語に共感します — 失敗しかけて立て直した仕事、お礼を言ってくれたお客様、この仕事をしている理由を思い出させてくれた瞬間など。そういう投稿はシェアされます。お話しする価値のあるお店のエピソードは?`,
  community_relations: `地域に貢献しているお店を応援したい人は多いです — 地元の少年団のスポンサー、災害後のボランティア、地域の慈善活動への協力、または単に近所で知られているお店であること。地域でやっていることで、見てもらいたいことは?`,
  humor_personality: `エンゲージメントが一番高い投稿は、必ずしもサービスについてではありません — 笑える、頷ける、友達をタグ付けしたくなる投稿です。職場の出来事、業界の小ネタ、「その場にいないと分からない」話など。日常の中で、面白い・親近感がある・リアルなことは?`,
};

// Inspiration block strings — the "Need inspiration?" card with content idea
// cards, the "Generate a New Idea" button, and the modal that opens for it.
// Localized 2026-05-25 so Thai/Spanish/Japanese users see the surrounding UI
// in their language too (the AI-generated card content is already per-language
// from the per-pool work in commit c1be574). scroll_for_more and modal_helper
// are functions because they take dynamic args (business name, etc).
// Lean-onboarding welcome toast — shown ONCE after a fresh signup, pointing
// at the docking station (the profile assistant icon column on the right
// edge of the viewport). Tells the user where to go for any further profile
// edits. Auto-fades after ~10s or on X dismiss. Trigger flag is set in
// ProfileAssistant.jsx handleNameSubmission and read in DockingStation
// on mount; cleared after first display so it never re-fires.
const WELCOME_TOAST_EN = {
  message: (name) => `Welcome ${name || 'there'}! Click here → to update your profile, add a logo, photo, and more any time.`,
};
const WELCOME_TOAST_TH = {
  message: (name) => `ยินดีต้อนรับ ${name || 'ค่ะ'}! กดที่นี่ → เพื่ออัปเดตโปรไฟล์ เพิ่มโลโก้ รูปภาพ และอื่นๆ ได้ทุกเมื่อ`,
};
const WELCOME_TOAST_ES = {
  message: (name) => `¡Bienvenido ${name || ''}! Toca aquí → para actualizar tu perfil, agregar logo, foto y más cuando quieras.`,
};
const WELCOME_TOAST_JA = {
  message: (name) => `${name || 'ようこそ'}さん、ようこそ! こちら → をクリックして、いつでもプロフィール、ロゴ、写真などを更新できます。`,
};

// Enrichment-in-progress banner — shown on OngoingProfileAssistant after a
// fresh signup while runPostSignupAnalysis (background job in signup.js)
// populates b_about_us / b_business_industry / b_lat / etc. The job takes
// ~30-60s; without this banner, new users see an empty profile and assume
// nothing happened. Banner auto-hides when b_about_us populates.
const ENRICHMENT_BANNER_EN = {
  message: "We're analyzing your website — your About will fill in shortly…",
};
const ENRICHMENT_BANNER_TH = {
  message: 'กำลังวิเคราะห์เว็บไซต์ของคุณอยู่ — ข้อมูล "เกี่ยวกับเรา" จะปรากฏในอีกสักครู่…',
};
const ENRICHMENT_BANNER_ES = {
  message: 'Estamos analizando tu sitio web — tu sección "Acerca de" aparecerá pronto…',
};
const ENRICHMENT_BANNER_JA = {
  message: 'ウェブサイトを分析中です — まもなく「自己紹介」が表示されます…',
};

// Focus-and-logistics modal — shown as a blocker on the Teams page when any of
// u_customer_focus / b_customer_focus / b_service_distance is null (the
// matching algorithm needs all three). Also reusable from the profile editor's
// "Update Focus and Logistics" button (Slice 2b). Lean-onboarding lifted these
// 3 questions out of the new-user wizard, so post-signup users WILL land here
// the first time they try to use Teams.
const FOCUS_LOGISTICS_EN = {
  blocker_title:       'Quick — 3 details before you can use Teams',
  blocker_subtitle:    "Teams match you with other businesses by who you serve. We need these to recommend the right partners.",
  editor_title:        'Update Focus and Logistics',
  editor_subtitle:     'Who you serve and how far you travel — used for team matching and recommendations.',
  q_biz_focus:         "Does your business sell to consumers, businesses, or both?",
  q_user_focus:        "Do YOU personally focus on selling to consumers, businesses, or both?",
  q_service_distance:  'How far from your location do you serve customers?',
  opt_b2c:             'Consumers only (B2C)',
  opt_b2b:             'Businesses only (B2B)',
  opt_b2bc:            'Both consumers and businesses (B2BC)',
  miles_label:         'miles from',
  zip_placeholder:     'Zip / Postal Code',
  zip_locked_hint:     'From your Google Business Profile — edit later from profile if needed.',
  error_zip_required:  'Please enter your zip / postal code.',
  save:                'Save',
  saving:              'Saving…',
  cancel:              'Cancel',
  error_required:      'Please answer all 3 questions.',
  error_invalid_miles: 'Please enter a number of miles between 1 and 5000.',
  error_save_failed:   (msg) => `Could not save: ${msg || 'please try again.'}`,
};
const FOCUS_LOGISTICS_TH = {
  blocker_title:       'รอแป๊บนึง — ตอบ 3 ข้อก่อนใช้ Teams',
  blocker_subtitle:    'Teams จะจับคู่คุณกับธุรกิจอื่นตามกลุ่มลูกค้าของคุณ ต้องมีข้อมูลนี้เพื่อแนะนำคู่ที่เหมาะค่ะ',
  editor_title:        'อัปเดตกลุ่มลูกค้าและพื้นที่บริการ',
  editor_subtitle:     'กลุ่มลูกค้าที่คุณให้บริการและระยะทางที่คุณเดินทาง — ใช้สำหรับจับคู่ทีมและการแนะนำ',
  q_biz_focus:         'ธุรกิจของคุณขายให้ผู้บริโภค ธุรกิจ หรือทั้งสองคะ?',
  q_user_focus:        'ตัวคุณเองเน้นขายให้ผู้บริโภค ธุรกิจ หรือทั้งสองคะ?',
  q_service_distance:  'คุณให้บริการลูกค้าในระยะทางกี่ไมล์จากที่ตั้งของคุณคะ?',
  opt_b2c:             'ผู้บริโภคเท่านั้น (B2C)',
  opt_b2b:             'ธุรกิจเท่านั้น (B2B)',
  opt_b2bc:            'ทั้งผู้บริโภคและธุรกิจ (B2BC)',
  miles_label:         'ไมล์จาก',
  zip_placeholder:     'รหัสไปรษณีย์',
  zip_locked_hint:     'จาก Google Business Profile ของคุณ — แก้ไขภายหลังได้ในหน้าโปรไฟล์',
  error_zip_required:  'กรุณาใส่รหัสไปรษณีย์',
  save:                'บันทึก',
  saving:              'กำลังบันทึก…',
  cancel:              'ยกเลิก',
  error_required:      'กรุณาตอบทั้ง 3 ข้อ',
  error_invalid_miles: 'กรุณาใส่ระยะทางเป็นตัวเลขระหว่าง 1 ถึง 5000 ไมล์',
  error_save_failed:   (msg) => `บันทึกไม่สำเร็จ: ${msg || 'กรุณาลองอีกครั้ง'}`,
};
const FOCUS_LOGISTICS_ES = {
  blocker_title:       'Rápido — 3 datos antes de usar Teams',
  blocker_subtitle:    'Teams te empareja con otros negocios según a quién atendés. Necesitamos esto para recomendarte los socios correctos.',
  editor_title:        'Actualizar enfoque y logística',
  editor_subtitle:     'A quién atendés y cuánto viajás — se usa para emparejamiento de equipos y recomendaciones.',
  q_biz_focus:         '¿Tu negocio vende a consumidores, empresas, o a ambos?',
  q_user_focus:        '¿VOS personalmente te enfocás en vender a consumidores, empresas, o a ambos?',
  q_service_distance:  '¿A qué distancia de tu ubicación atendés a clientes?',
  opt_b2c:             'Solo consumidores (B2C)',
  opt_b2b:             'Solo empresas (B2B)',
  opt_b2bc:            'Consumidores y empresas (B2BC)',
  miles_label:         'millas desde',
  zip_placeholder:     'Código postal',
  zip_locked_hint:     'De tu Google Business Profile — editá luego desde tu perfil si hace falta.',
  error_zip_required:  'Por favor ingresá tu código postal.',
  save:                'Guardar',
  saving:              'Guardando…',
  cancel:              'Cancelar',
  error_required:      'Por favor respondé las 3 preguntas.',
  error_invalid_miles: 'Por favor ingresá un número de millas entre 1 y 5000.',
  error_save_failed:   (msg) => `No se pudo guardar: ${msg || 'intentalo de nuevo.'}`,
};
const FOCUS_LOGISTICS_JA = {
  blocker_title:       'Teamsを使う前に — 3つの質問にお答えください',
  blocker_subtitle:    'Teamsはお客様のターゲットに合わせて他のビジネスとマッチングします。最適なパートナーを推薦するために必要な情報です。',
  editor_title:        'ターゲットとサービスエリアを更新',
  editor_subtitle:     '誰にサービスを提供し、どこまで対応するか — チームマッチングと推薦に使用されます。',
  q_biz_focus:         'あなたのビジネスは消費者、企業、または両方に販売していますか?',
  q_user_focus:        'あなた個人は消費者、企業、または両方への販売に注力していますか?',
  q_service_distance:  'お客様の所在地から何マイル先までサービスを提供していますか?',
  opt_b2c:             '消費者のみ (B2C)',
  opt_b2b:             '企業のみ (B2B)',
  opt_b2bc:            '消費者と企業の両方 (B2BC)',
  miles_label:         'マイル (起点:',
  zip_placeholder:     '郵便番号',
  zip_locked_hint:     'Google Business Profileから取得 — 後でプロフィールから編集できます。',
  error_zip_required:  '郵便番号を入力してください。',
  save:                '保存',
  saving:              '保存中…',
  cancel:              'キャンセル',
  error_required:      '3つの質問すべてにお答えください。',
  error_invalid_miles: '1から5000の間のマイル数を入力してください。',
  error_save_failed:   (msg) => `保存できませんでした: ${msg || 'もう一度お試しください。'}`,
};

const INSPIRATION_EN = {
  need_inspiration:     'Need inspiration?',
  scroll_for_more:      (biz, category) => category ? `Scroll for more "${category}" ideas for ${biz || 'your business'}` : `Scroll for more ideas for ${biz || 'your business'}`,
  dismiss:              'Dismiss',
  generating_ideas:     'Generating ideas for your profession...',
  generate_new_btn:     'Generate a New Idea',
  generate_new_subtitle:'Crowd sourced by profession',
  generate_new_title:   'Generate a new idea (shared with your whole profession)',
  generating_new:       'Generating a new idea...',
  use_this_idea:        'Use This Idea →',
  video_badge:          'Video',
  image_badge:          'Image',
  modal_title:          'Generate a new inspiration',
  modal_cancel_title:   'Cancel',
  modal_label:          'Focus the new idea on a specific topic (optional)',
  modal_helper:         'Leave blank for a random fresh idea. If your business covers multiple product lines, this lets you steer the AI at one of them.',
  modal_cancel:         'Cancel',
  modal_submit:         'Generate',
};
const INSPIRATION_TH = {
  need_inspiration:     'ต้องการแรงบันดาลใจไหม?',
  scroll_for_more:      (biz, category) => category ? `เลื่อนดูไอเดีย "${category}" เพิ่มเติมสำหรับ ${biz || 'ธุรกิจของคุณ'}` : `เลื่อนดูไอเดียเพิ่มเติมสำหรับ ${biz || 'ธุรกิจของคุณ'}`,
  dismiss:              'ปิด',
  generating_ideas:     'กำลังสร้างไอเดียสำหรับอาชีพของคุณ...',
  generate_new_btn:     'สร้างไอเดียใหม่',
  generate_new_subtitle:'รวบรวมจากคนในอาชีพเดียวกัน',
  generate_new_title:   'สร้างไอเดียใหม่ (แชร์กับคนในอาชีพเดียวกันทั้งหมด)',
  generating_new:       'กำลังสร้างไอเดียใหม่...',
  use_this_idea:        'ใช้ไอเดียนี้ →',
  video_badge:          'วิดีโอ',
  image_badge:          'รูปภาพ',
  modal_title:          'สร้างแรงบันดาลใจใหม่',
  modal_cancel_title:   'ยกเลิก',
  modal_label:          'เจาะจงหัวข้อที่อยากให้ AI เน้น (ไม่บังคับ)',
  modal_helper:         'ถ้าเว้นว่าง AI จะสร้างไอเดียใหม่แบบสุ่ม ถ้าธุรกิจของคุณมีหลายบริการ ช่องนี้ช่วยบอก AI ให้เน้นบริการไหนเป็นพิเศษ',
  modal_cancel:         'ยกเลิก',
  modal_submit:         'สร้าง',
};
const INSPIRATION_ES = {
  need_inspiration:     '¿Necesitas inspiración?',
  scroll_for_more:      (biz, category) => category ? `Desliza para más ideas de "${category}" para ${biz || 'tu negocio'}` : `Desliza para más ideas para ${biz || 'tu negocio'}`,
  dismiss:              'Descartar',
  generating_ideas:     'Generando ideas para tu profesión...',
  generate_new_btn:     'Generar Idea Nueva',
  generate_new_subtitle:'Aportado por tu profesión',
  generate_new_title:   'Generar una idea nueva (compartida con toda tu profesión)',
  generating_new:       'Generando idea nueva...',
  use_this_idea:        'Usar esta idea →',
  video_badge:          'Video',
  image_badge:          'Imagen',
  modal_title:          'Generar nueva inspiración',
  modal_cancel_title:   'Cancelar',
  modal_label:          'Enfoca la idea en un tema específico (opcional)',
  modal_helper:         'Déjalo en blanco para una idea aleatoria. Si tu negocio cubre varias líneas de productos, este campo te deja apuntar a una específica.',
  modal_cancel:         'Cancelar',
  modal_submit:         'Generar',
};
const INSPIRATION_JA = {
  need_inspiration:     'アイデアが必要ですか?',
  scroll_for_more:      (biz, category) => category ? `${biz || 'あなたのビジネス'}の「${category}」アイデアをもっと見る` : `${biz || 'あなたのビジネス'}のアイデアをもっと見る`,
  dismiss:              '閉じる',
  generating_ideas:     'あなたの職業に合うアイデアを作成中...',
  generate_new_btn:     '新しいアイデアを生成',
  generate_new_subtitle:'同じ職業のユーザーが投稿',
  generate_new_title:   '新しいアイデアを生成 (同業者全員と共有)',
  generating_new:       '新しいアイデアを生成中...',
  use_this_idea:        'このアイデアを使う →',
  video_badge:          '動画',
  image_badge:          '画像',
  modal_title:          '新しいアイデアを生成',
  modal_cancel_title:   'キャンセル',
  modal_label:          '特定のトピックに絞る (任意)',
  modal_helper:         '空欄ならランダムな新アイデアを生成します。複数のサービスがある場合、ここで一つに焦点を絞れます。',
  modal_cancel:         'キャンセル',
  modal_submit:         '生成',
};

const STRINGS = {
  en: {
    sections: SECTION_OPENINGS_EN,
    inspiration: INSPIRATION_EN,
    enrichment_banner: ENRICHMENT_BANNER_EN,
    welcome_toast: WELCOME_TOAST_EN,
    focus_logistics: FOCUS_LOGISTICS_EN,
    image_updated:        'Image updated. Your draft stays intact.',
    follow_up_fallback:   `Got it — what do you want people to do or feel when they see this?`,
    generating:           `Generating your headline and captions now...`,
    updating:             `Updating captions...`,
    results_intro:        `Here's your headline for the image:`,
    results_updated_intro:`Updated headline:`,
    results_outro:        `Want to adjust anything? Tell me what to change, or click "Preview by Social Platform" when it looks right.`,
    adjust_or_preview:    `Tell me what to change, or click "Preview by Social Platform" when it looks right.`,
    error_generic:        (msg) => `Something went wrong. ${msg || 'Please try again.'}`,
    error_network:        `Network error — couldn't reach the AI service. Please try again.`,
    input_placeholder:    'Type your answer…',
  },
  th: {
    sections: SECTION_OPENINGS_TH,
    inspiration: INSPIRATION_TH,
    enrichment_banner: ENRICHMENT_BANNER_TH,
    welcome_toast: WELCOME_TOAST_TH,
    focus_logistics: FOCUS_LOGISTICS_TH,
    image_updated:        'รูปภาพอัปเดตแล้วค่ะ ดราฟต์ของคุณยังอยู่ครบ',
    follow_up_fallback:   `เข้าใจแล้วค่ะ — อยากให้คนที่เห็นโพสต์นี้ทำอะไรหรือรู้สึกยังไงคะ?`,
    generating:           `กำลังสร้างหัวข้อและแคปชั่นให้คุณค่ะ...`,
    updating:             `กำลังแก้ไขแคปชั่น...`,
    results_intro:        `นี่คือหัวข้อสำหรับรูปของคุณค่ะ:`,
    results_updated_intro:`หัวข้อใหม่:`,
    results_outro:        `อยากปรับอะไรไหมคะ? บอกได้เลย หรือถ้าโอเคแล้วกด "Preview by Social Platform" เลย`,
    adjust_or_preview:    `บอกได้เลยว่าอยากเปลี่ยนอะไร หรือกด "Preview by Social Platform" ถ้าโอเคแล้ว`,
    error_generic:        (msg) => `มีบางอย่างผิดพลาดค่ะ ${msg || 'ลองอีกครั้งนะคะ'}`,
    error_network:        `เครือข่ายมีปัญหาค่ะ — ติดต่อ AI ไม่ได้ ลองอีกครั้งนะคะ`,
    input_placeholder:    'พิมพ์คำตอบของคุณ…',
  },
  es: {
    sections: SECTION_OPENINGS_ES,
    inspiration: INSPIRATION_ES,
    enrichment_banner: ENRICHMENT_BANNER_ES,
    welcome_toast: WELCOME_TOAST_ES,
    focus_logistics: FOCUS_LOGISTICS_ES,
    image_updated:        'Imagen actualizada. Tu borrador sigue intacto.',
    follow_up_fallback:   `Entendido — ¿qué querés que la gente haga o sienta cuando vea esto?`,
    generating:           `Generando tu titular y captions ahora...`,
    updating:             `Actualizando captions...`,
    results_intro:        `Acá está tu titular para la imagen:`,
    results_updated_intro:`Titular actualizado:`,
    results_outro:        `¿Querés ajustar algo? Decime qué cambiar, o tocá "Preview by Social Platform" cuando esté listo.`,
    adjust_or_preview:    `Decime qué cambiar, o tocá "Preview by Social Platform" cuando esté listo.`,
    error_generic:        (msg) => `Algo salió mal. ${msg || 'Intentalo de nuevo.'}`,
    error_network:        `Error de red — no se pudo contactar el servicio de AI. Intentalo de nuevo.`,
    input_placeholder:    'Escribí tu respuesta…',
  },
  ja: {
    sections: SECTION_OPENINGS_JA,
    inspiration: INSPIRATION_JA,
    enrichment_banner: ENRICHMENT_BANNER_JA,
    welcome_toast: WELCOME_TOAST_JA,
    focus_logistics: FOCUS_LOGISTICS_JA,
    image_updated:        '画像を更新しました。下書きはそのままです。',
    follow_up_fallback:   `分かりました — この投稿を見た人にどう感じてほしい・何をしてほしいですか?`,
    generating:           `見出しとキャプションを作成中です...`,
    updating:             `キャプションを更新中...`,
    results_intro:        `画像用の見出しです:`,
    results_updated_intro:`更新した見出し:`,
    results_outro:        `何か調整しますか?変更点を教えてください。もしくはOKなら「Preview by Social Platform」を押してください。`,
    adjust_or_preview:    `変更点を教えてください。もしくはOKなら「Preview by Social Platform」を押してください。`,
    error_generic:        (msg) => `エラーが発生しました。${msg || 'もう一度お試しください。'}`,
    error_network:        `ネットワークエラー — AIサービスに接続できませんでした。もう一度お試しください。`,
    input_placeholder:    '回答を入力…',
  },
};

/**
 * Get the strings table for a given language. Falls back to English when
 * the language is unknown, null, or not yet loaded. Safe to call before
 * the user's profile has loaded.
 */
export function getStrings(lang) {
  return STRINGS[lang] || STRINGS.en;
}

/**
 * Get the section opening string for a section key in the given language.
 * Falls back to how_things_work (the existing DEFAULT_SCRIPT behavior in
 * DesignCenterAIAssist) if the section key is unknown.
 */
export function getSectionOpening(lang, sectionKey) {
  const s = getStrings(lang);
  return s.sections[sectionKey] || s.sections.how_things_work;
}
